import { Apis, ViewModels } from '../../../../services/backendServices';
import {
    ParsedInputParameter,
    ParsedSettingValueType,
    ParsedXrefParameters,
    processXrefParameters,
} from '../../../CustomJSRulesV2/Utils/processXrefParameters';
import { ScriptCompiler } from '../../Utility';
import { RuntimeRuleLibrary, RuntimeRuleNonLibrary } from './RuntimeRule';
import { api } from '../../../../services/api';
import { createRulesFilter } from './RuntimeRuleFetcher.filter';
import { orderBy } from '../../../../services/utils/orderBy';
import { ascendingOrderByKey } from '../../../../utils/sorting';
import { dummyScript, dummyScriptName } from './dummyCustomJSRuleScript';

export function getTestScriptForDevelopment(src?: string) {
    if (src !== dummyScriptName || !dummyScriptName.length) {
        return null;
    }

    return dummyScript;
}

function createConfigParamsMap(xref: ParsedXrefParameters, vendorClass?: string) {
    const legacyInputsExistingParams = new Set<string>();
    const newInputsExistingParams = new Set<string>();
    const configParams = new Map<string, ParsedSettingValueType | Record<string, ParsedSettingValueType>>();

    xref.ruleParameters.forEach((ruleParam) => {
        const isEnabledByDefault = ruleParam.DefaultEnabled !== undefined ? ruleParam.DefaultEnabled : true; // old parameters without "DefaultEnabled" are considered enabled by default

        if (ruleParam.Inputs && ruleParam.Inputs.length > 0) {
            newInputsExistingParams.add(ruleParam.Name);
            const inputsArray = ruleParam.Inputs.map((input) => [input.InputKey, input.DefaultValue]);
            const parsedInputObject: ParsedSettingValueType = Object.fromEntries(inputsArray);
            if (isEnabledByDefault) {
                configParams.set(ruleParam.Name, parsedInputObject);
            }
        } else if (ruleParam.DefaultValue !== undefined) {
            legacyInputsExistingParams.add(ruleParam.Name);
            if (isEnabledByDefault) {
                configParams.set(ruleParam.Name, ruleParam.DefaultValue);
            }
        }
    });

    const performOverride = (params: ParsedInputParameter[]) => {
        params.forEach((param) => {
            // params could exist in the xref input parameters or input parameter override input parameters
            // that don't exist in the rule's param list. this prevents those from appearing in the config
            // param map.
            if (legacyInputsExistingParams.has(param.Name)) {
                if (!param.Enabled) {
                    configParams.delete(param.Name);
                } else {
                    configParams.set(param.Name, param.Value);
                }
            } else if (newInputsExistingParams.has(param.Name)) {
                if (!param.Enabled) {
                    configParams.delete(param.Name);
                } else if (
                    param.InputValues &&
                    typeof param.InputValues === 'object' &&
                    Object.entries(param.InputValues).length > 0
                ) {
                    configParams.set(param.Name, param.InputValues);
                }
            }
        });
    };

    performOverride(xref.xrefParameterValues);

    const defaultOverride = xref.parameterOverrides.find((o) => !o.vendorClasses.length);

    /**
     * When the document has no vendorClass,
     * we'll use the default override if it's available.
     */
    const vendorClassOverride =
        vendorClass === undefined
            ? defaultOverride
            : xref.parameterOverrides.find((o) => o.vendorClasses.includes(vendorClass)) ?? defaultOverride;

    if (vendorClassOverride) {
        performOverride(vendorClassOverride.parameterValues);
    }

    return configParams;
}

async function fetchAndCompileScript(src: string) {
    const { data: scriptSrc } = await api().get(`../TransceptaAPICode/BuyerCustomJSFiles/${src}`);

    return ScriptCompiler.compileScript(getTestScriptForDevelopment(src) ?? scriptSrc);
}

async function createRule({
    xref,
    vendorClass,
    dpsRuleName = null,
}: {
    xref: ViewModels.BuyerCustomJSRuleMappingXrefViewModel;
    vendorClass?: string;
    dpsRuleName?: string | null;
}) {
    if (!xref.BuyerCustomJSRule) {
        throw new Error('Expected BuyerCustomJSRule to exist.');
    }

    const configParams = createConfigParamsMap(processXrefParameters(xref), vendorClass);
    const isLibrary = xref.BuyerCustomJSRule?.RuleType === ViewModels.RuleType.Library;
    const script = await fetchAndCompileScript(xref.BuyerCustomJSRule.JavaScriptFileName);

    if (isLibrary) {
        return {
            name: xref.BuyerCustomJSRule.Name,
            dpsRuleName,
            script,
            isLibrary: true,
            configParams,
        } as RuntimeRuleLibrary;
    }
    return {
        name: xref.BuyerCustomJSRule.Name,
        dpsRuleName,
        script,
        isLibrary: false,
        configParams,
        ordinal: xref.Ordinal,
    } as RuntimeRuleNonLibrary;
}

function filterBuyerCustomJSRuleMappingsByVendorClass(
    ruleMappings: ViewModels.BuyerCustomJSRuleMappingViewModel[],
    vendorClass: string | undefined
) {
    return ruleMappings.filter(
        (mapping) => mapping.VendorClasses == null || mapping.VendorClasses?.split(',').some((vc) => vc === vendorClass)
    );
}

export class RuntimeRuleFetcher {
    private api = new Apis.BuyerCustomJSRuleMappingApi();

    async fetchMapping(
        params: Apis.GetBuyerCustomJSRulesMappingByExecutionContextParams & {
            vendorClass?: string;
        },
        filterFn?: (mappings: ViewModels.BuyerCustomJSRuleMappingViewModel) => boolean
    ): Promise<ViewModels.BuyerCustomJSRuleMappingViewModel | null> {
        const { vendorClass, ...rest } = params;

        const $filter = createRulesFilter({ vendorClass });

        const response = await this.api.getBuyerCustomJSRulesMappingByExecutionContext({
            ...rest,
            $filter,
            $orderby: orderBy([{ field: 'BuyerCompanyId', sort: 'desc' }]),
        });

        /**
         * The $filter created by createRulesFilter returns more results than it should because it uses `contains()` instead of `eq()`.
         * It was decided that we should add filtering logic programmatically instead of modifying the $filter string.
         */
        const mappingsFilteredByVendorClass = filterBuyerCustomJSRuleMappingsByVendorClass(response.data, vendorClass);

        const mappings =
            filterFn != null ? mappingsFilteredByVendorClass.filter(filterFn) : mappingsFilteredByVendorClass;

        if (mappings.length === 0) {
            return null;
        }

        const mapping = mappings[0];

        return mapping;
    }

    async mapMappingVMToRuntimeRules(
        vm: ViewModels.BuyerCustomJSRuleMappingViewModel | null,
        targetVendorClass: string | undefined
    ) {
        if (vm == null) {
            return [];
        }

        const rules = await Promise.all(
            vm.BuyerCustomJSRuleMappingXrefs.sort(ascendingOrderByKey('Ordinal')).map((xref) =>
                createRule({ xref, vendorClass: targetVendorClass, dpsRuleName: vm.DPSRuleName })
            )
        );

        return rules;
    }
}
